import { isEmpty } from 'lodash/fp'
import { SearchResults, SearchState, StateResultsProvided } from 'react-instantsearch-core'
import { connectStateResults } from 'react-instantsearch-dom'
import styled from 'styled-components'

import { clickableImagesContent } from 'homepage/clickableImagesContent'
import { t } from 'localization'
import TrendingProducts from 'search/components/TrendingProducts'
import { ResultsState } from 'search/types/ResultState'
import ClickableImagesRow from 'shared/components/ClickableImagesRow'
import GridHeader from 'shared/components/GridHeader'
import { colors, media } from 'shared/lib'
import { CSSearchState } from 'shared/utils/search/cs-utils'

const TP = 'search.components.AlgoliaResults'

/**
 * Component to conditionally display children based on algolia search results
 *
 * Need to set display none instead of conditionally render children because of
 * issues with algolia react components. See:
 * https://github.com/algolia/react-instantsearch/issues/137#issuecomment-349385276
 * for more info
 *
 * Usage:
 * <InstantSearch
 *     resultsState={resultsState}
 * >
 *   <AlgoliaResults>
 *     <Hits />
 *     <Filters />
 *   </AlgoliaResults>
 * </InstantSearch>
 */
interface IAlgoliaResults extends StateResultsProvided {
  noResultsState: ResultsState | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any
}

type AlgoliaResultsNoConnectProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any
  isAlgolia?: boolean
  noResultsState: ResultsState | null
  searchResults: SearchResults<ResultsState>
  searchState: SearchState | CSSearchState
}

export const AlgoliaResultsNoConnect = ({
  searchResults,
  searchState,
  noResultsState,
  children,
  isAlgolia,
}: AlgoliaResultsNoConnectProps) => {
  const hasNoResults = isAlgolia ? searchResults?.nbHits === 0 : (searchResults || [])?.length === 0

  const onlyQueryTermActive =
    !!searchState?.query && isEmpty(searchState?.refinementList) && isEmpty(searchState?.range)

  const noResultsTitle =
    searchState && !isEmpty(searchState.query)
      ? t(`${TP}.noResultsWithQuery`, {
          defaultValue: 'We could not find anything for "{searchStateQuery}"',
          searchStateQuery: searchState.query,
        })
      : t(`${TP}.noResults`, 'No results found')

  const displayNoResultsGrid = hasNoResults && onlyQueryTermActive

  return (
    <>
      {displayNoResultsGrid && (
        <>
          <GridHeader
            as="h2"
            title={noResultsTitle}
            description={t(
              `${TP}.noResultTitle`,
              'Check the spelling, search with a new term, or use links above to find what you are looking for.',
            )}
            textAlign="center"
          />
          {noResultsState && (
            <TrendingProducts
              resultsState={isAlgolia === false ? undefined : noResultsState}
              isAlgolia={isAlgolia}
              hits={isAlgolia === false ? noResultsState : []}
            />
          )}
          <ClickableImagesRow imagesList={clickableImagesContent()} />
        </>
      )}
      {!displayNoResultsGrid && <GridContainer>{children}</GridContainer>}
    </>
  )
}

const AlgoliaResults = ({
  searchResults,
  searchState,
  noResultsState,
  children,
}: IAlgoliaResults) => {
  return (
    <AlgoliaResultsNoConnect
      searchResults={searchResults}
      searchState={searchState}
      noResultsState={noResultsState}
      isAlgolia={true}
    >
      {children}
    </AlgoliaResultsNoConnect>
  )
}

export default connectStateResults<IAlgoliaResults>(AlgoliaResults)

const GridContainer = styled.div`
  background-color: ${colors.FC2_OFF_WHITE};
  ${media.large`
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`
